import React, { useCallback, useEffect, useState } from 'react';
import { useAddress } from '@thirdweb-dev/react';
import { IdentityContext, Identity, IdentityTier, refreshSBTStatus } from '../lib/identity';
import WalletConnectPill from './WalletConnectPill';

// Wallet-backed identity for the Agoracle Global route.
//
// Reads the connected address via Thirdweb's useAddress hook. When the
// citizen has not yet connected, `id` is null — downstream code (the
// ensureProfile effect, placeBet handler, useProfileRealtime) is
// already guarded to no-op on null, and MultiPage renders a "Connect
// to the Agora" prompt in place of the balance pill.

function shortAddress(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

const FORCE_EVENT = 'agoracle:tier-change';

export default function WalletIdentityProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const address = useAddress();
  const [tier, setTier] = useState<IdentityTier>(0);

  const runProbe = useCallback(() => {
    let cancelled = false;
    refreshSBTStatus(address ?? null).then(t => { if (!cancelled) setTier(t); });
    return () => { cancelled = true; };
  }, [address]);

  // Re-probe whenever the connected address flips OR the architect
  // forces a tier override via window.__agoracleForceTier(...). The
  // event-based channel is what lets the force happen without a reload.
  useEffect(() => {
    const cancel = runProbe();
    window.addEventListener(FORCE_EVENT, runProbe);
    return () => {
      cancel();
      window.removeEventListener(FORCE_EVENT, runProbe);
    };
  }, [runProbe]);

  const pill = <WalletConnectPill />;

  const value: Identity = {
    id:        address ?? null,
    mode:      'wallet',
    connected: !!address,
    tier,
    refresh:   runProbe,
    pill,
    label:     address ? shortAddress(address) : 'Not connected',
  };

  return (
    <IdentityContext.Provider value={value}>
      {children}
    </IdentityContext.Provider>
  );
}
